import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Outlet, redirect, type RouteComponent } from "@tanstack/react-router";
import { getSession, refreshSession } from "./lib/session";
import { AppShell } from "./shell/AppShell";
import { AcceptInvitePage, ForgotPage, LoginPage, RegisterPage, ResetPage } from "./features/auth/AuthPages";
import { CrmRoutes } from "./features/crm/routes";
import { SalesRoutes } from "./features/sales/routes";
import { ContractsRoutes } from "./features/contracts/routes";
import { ExpensesRoutes } from "./features/expenses/routes";
import { MarketingRoutes } from "./features/marketing/routes";
import { SettingsRoutes } from "./features/settings/routes";

// pagine caricate a richiesta: ogni area è un chunk separato
const OverviewL = lazyRouteComponent(() => import("./features/dashboard/Overview"), "Overview");
const ModuleOverviewL = lazyRouteComponent(() => import("./features/dashboard/ModuleOverview"), "ModuleOverview");
const MyTasksL = lazyRouteComponent(() => import("./features/projects/Pages"), "MyTasks");
const ProjectPageL = lazyRouteComponent(() => import("./features/projects/Pages"), "ProjectPage");
const ProjectsListL = lazyRouteComponent(() => import("./features/projects/Pages"), "ProjectsList");
const TimesheetL = lazyRouteComponent(() => import("./features/projects/Timesheet"), "TimesheetPage");
const TimesheetApprovalsL = lazyRouteComponent(() => import("./features/projects/Timesheet"), "TimesheetApprovals");
const CapacityL = lazyRouteComponent(() => import("./features/projects/Timesheet"), "CapacityPage");
const AssistantPageL = lazyRouteComponent(() => import("./features/assistant/Assistant"), "AssistantPage");
const NotificationsPageL = lazyRouteComponent(() => import("./features/dashboard/Inbox"), "NotificationsPage");
const TodayPageL = lazyRouteComponent(() => import("./features/dashboard/Inbox"), "TodayPage");
const DrivePageL = lazyRouteComponent(() => import("./features/documents/Drive"), "DrivePage");
const PublicQuoteL = lazyRouteComponent(() => import("./features/sales/PublicQuote"), "PublicQuote");
const PlatformInvoicingL = lazyRouteComponent(() => import("./features/platform/Platform"), "PlatformInvoicing");
const PlatformOverviewL = lazyRouteComponent(() => import("./features/platform/Platform"), "PlatformOverview");
const PlatformPaymentsL = lazyRouteComponent(() => import("./features/platform/Platform"), "PlatformPayments");
const PlatformTenantsL = lazyRouteComponent(() => import("./features/platform/Platform"), "PlatformTenants");
const PlatformUsersL = lazyRouteComponent(() => import("./features/platform/Platform"), "PlatformUsers");

const root = createRootRoute({ component: Outlet });

// pagine pubbliche
const login = createRoute({ getParentRoute: () => root, path: "/login", component: LoginPage });
const register = createRoute({ getParentRoute: () => root, path: "/register", component: RegisterPage });
const forgot = createRoute({ getParentRoute: () => root, path: "/forgot-password", component: ForgotPage });
const reset = createRoute({ getParentRoute: () => root, path: "/reset-password", component: ResetPage });
const invite = createRoute({ getParentRoute: () => root, path: "/accept-invite", component: AcceptInvitePage });
const publicQuote = createRoute({ getParentRoute: () => root, path: "/q/$token", component: PublicQuoteL });
// GitHub App e consumer Bitbucket hanno ancora il callback di Planner: lo giriamo all'API con la stessa query
const gitCallback = createRoute({
  getParentRoute: () => root,
  path: "/integrations/git/$provider/callback",
  beforeLoad: ({ params }) => window.location.replace(`/api/v1/git/${encodeURIComponent(params.provider)}/callback${window.location.search}`),
  component: () => null,
});

/** Area autenticata: se manca la sessione prova il refresh dal cookie. */
export const app = createRoute({
  getParentRoute: () => root,
  id: "app",
  component: AppShell,
  beforeLoad: async ({ location }) => {
    if (getSession()) return;
    const s = await refreshSession();
    if (!s) throw redirect({ to: "/login", search: { next: location.href } as never });
  },
});

export type At = <P extends string>(path: P, component: RouteComponent) => ReturnType<typeof createAt<P>>;
function createAt<P extends string>(path: P, component: RouteComponent) {
  return createRoute({ getParentRoute: () => app, path, component });
}
const at = createAt;

const routeTree = root.addChildren([
  login,
  register,
  forgot,
  reset,
  invite,
  publicQuote,
  gitCallback,
  app.addChildren([
    at("/", OverviewL),
    // panoramica di ogni modulo (il titolo del modulo nel menu porta qui)
    ...(["projects", "crm", "sales", "contracts", "expenses", "marketing"] as const).map((k) => at(`/${k}/overview`, ModuleOverviewL)),
    at("/today", TodayPageL),
    at("/notifications", NotificationsPageL),
    at("/documents", DrivePageL),
    at("/projects", ProjectsListL),
    at("/projects/my-tasks", MyTasksL),
    at("/projects/timesheet", TimesheetL),
    at("/projects/timesheet/approvals", TimesheetApprovalsL),
    at("/projects/capacity", CapacityL),
    at("/projects/$projectId", ProjectPageL),
    at("/projects/$projectId/$view", ProjectPageL),
    ...CrmRoutes(at),
    ...SalesRoutes(at),
    ...ContractsRoutes(at),
    ...ExpensesRoutes(at),
    ...MarketingRoutes(at),
    at("/assistant", AssistantPageL),
    ...SettingsRoutes(at),
    at("/platform", PlatformOverviewL),
    at("/platform/tenants", PlatformTenantsL),
    at("/platform/users", PlatformUsersL),
    at("/platform/payments", PlatformPaymentsL),
    at("/platform/invoicing", PlatformInvoicingL),
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
  // transizione del browser solo cambiando pagina e mai con "riduci movimento"; niente transizione se si apre
  // un dettaglio (task o deal): il pannello laterale ha già la sua animazione e i due effetti si accavallerebbero
  defaultViewTransition: {
    types: ({ pathChanged, toLocation }) => {
      const opensDetail = !!(toLocation.search as { task?: string }).task || toLocation.pathname.startsWith("/crm/deals/");
      return pathChanged && !opensDetail && !window.matchMedia("(prefers-reduced-motion: reduce)").matches ? ["page"] : false;
    },
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
